// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/Thread.test.tsx
// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import { Button, Flex, Text } from "@radix-ui/themes";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAskV2AttachmentDraft } from "./AskV2AttachmentDraftContext";
import { AskV2ThreadFindings } from "./AskV2ThreadFindings";
import { CommentContextChip } from "./CommentContextChip";
import { withOpenItemChip } from "./openItemChip";
import { type AskV2Attachment, type AskV2ThreadMessage } from "./types";
import { type AskV2TransportApi } from "./useAskV2Transport";

import { AskPunttThread } from "../../components/ask-puntt-thread";
import { collectLiveFindings } from "../rail/collectRailContent";

type FeedMember = {
  _id: string;
  name?: string | null;
  email?: string | null;
  avatar?: string | null;
};

type ThreadProps = {
  transport: AskV2TransportApi;
  presentation?: "chat" | "feed";
  feedActorName?: string;
  feedActorAvatar?: string;
  feedMembers?: FeedMember[];
  currentUserId?: string;
  /**
   * Draw verified findings in this thread. Off on the review workspace feed,
   * which already lists them above its comments.
   */
  showEarlyFindings?: boolean;
  /** Hide thread findings whose description already appears on the open-items rail. */
  suppressFindingDescriptions?: ReadonlySet<string>;
  /**
   * Read-only viewer. Four affordances change here, and this list is the
   * whole of it — each is a write the page's gated transport cannot swallow
   * safely, and each is enforced by a separate term below:
   *
   * 1. `chatId` is withheld from `AskPunttThread`, so `ChatMessage` draws no
   *    per-row thumbs. Feedback POSTs its own mutation, never transport.
   * 2. `onSubmitAnswers` is withheld, so the agent's question form draws no
   *    live Submit. A no-op handler would still draw one.
   * 3. The failed-row Retry button is dropped. The failure notice stays.
   * 4. `readOnly` is handed to `AskV2ThreadFindings`, so a streamed finding
   *    draws no judge control. That POST goes to christo, not transport.
   *
   * All four are refusals, not silent no-ops: the composer lock already
   * refuses the send, so an affordance that looks live and does nothing is
   * the worse outcome. The per-site comments below carry the reasoning for
   * each term; this is the one place that names all four together.
   */
  readOnly?: boolean;
};

function transportMessageKey(message: AskV2ThreadMessage): string {
  return message.serverId ?? message.clientId;
}

export function AskV2Thread({
  transport,
  presentation = "chat",
  feedActorName,
  feedActorAvatar,
  feedMembers,
  currentUserId,
  showEarlyFindings = false,
  suppressFindingDescriptions,
  readOnly = false,
}: ThreadProps) {
  const { t } = useTranslation("ui");
  const attachmentDraft = useAskV2AttachmentDraft();
  const attachmentDraftBlocked =
    attachmentDraft?.hasPending === true || attachmentDraft?.hasError === true;
  const formDisabledReason =
    attachmentDraft?.hasError === true
      ? ("upload-failed" as const)
      : attachmentDraft?.hasPending === true
        ? ("upload-pending" as const)
        : transport.inFlight
          ? ("turn-in-flight" as const)
          : undefined;

  const normalizedFeedMembers = useMemo(
    () =>
      feedMembers?.map((member) => ({
        _id: member._id,
        name: member.name ?? undefined,
        email: member.email ?? undefined,
        avatar: member.avatar,
      })),
    [feedMembers],
  );

  const { send } = transport;
  // A form the agent drew is answered as the next message, in words. Pass the
  // formatted answer straight to send() so it never flashes in the composer.
  // Read-only viewers are refused here — the backend would 403 the send.
  const submitAnswers = useCallback(
    (formattedAnswers: string, attachments?: AskV2Attachment[]) => {
      if (readOnly || attachmentDraftBlocked) return;
      const sharedAttachments = attachmentDraft?.readyAttachments ?? [];
      const byKey = new Map(
        [...sharedAttachments, ...(attachments ?? [])].map((attachment) => [
          attachment.key,
          attachment,
        ]),
      );
      void send({
        message: formattedAnswers,
        attachments: byKey.size > 0 ? Array.from(byKey.values()) : undefined,
      }).then((accepted) => {
        if (accepted) attachmentDraft?.consumeAttachments(sharedAttachments);
      });
    },
    [attachmentDraft, attachmentDraftBlocked, send, readOnly],
  );

  // ChatMessage takes `chatId` for exactly one thing: the per-row thumbs —
  // the `useSubmitAskPunttFeedback` mutation, the `chatId` term in its
  // `showFeedback` guard, and that handler's analytics are its only readers.
  // Feedback never touches AskV2TransportApi, so the page's gated transport
  // cannot cover it, and `AskPunttThreadProps` declares no read-only flag to
  // forward — passing one is a compile error, not a dropped signal. So the
  // gate rides the props that component DOES declare, and the feedback half
  // of it is derived here, in one place, so this reasoning cannot drift away
  // from the `readOnly` term that enforces it: no chatId, no thumbs, so the
  // 403 the composer lock exists to avoid can never be fired from a row.
  const threadChatId = readOnly ? undefined : (transport.chatId ?? undefined);

  // why: the open item block stays in the stored message for the model; the
  // bubble shows it as the chip below and keeps the person's words (L2.19).
  const shownMessages = useMemo(
    () => transport.displayMessages.map(withOpenItemChip),
    [transport.displayMessages],
  );
  const transportById = useMemo(() => {
    const map = new Map<string, AskV2ThreadMessage>();
    for (const message of transport.messages) {
      map.set(transportMessageKey(message), message);
      map.set(message.clientId, message);
    }
    return map;
  }, [transport.messages]);

  const showMessageThread =
    transport.displayMessages.length > 0 || transport.messages.length > 0;
  // why: the review workspace feed already lists these above its comments, so only the main thread opts in
  const liveFindingsRaw = showEarlyFindings
    ? collectLiveFindings(
        transport.historyMessages,
        transport.streamSnapshot?.verifiedFindings,
      )
    : null;
  const liveFindings =
    liveFindingsRaw && suppressFindingDescriptions?.size
      ? liveFindingsRaw.filter(
          (finding) =>
            !suppressFindingDescriptions.has(finding.description.trim()),
        )
      : liveFindingsRaw;
  const liveFindingsVisible =
    liveFindings && liveFindings.length > 0 ? liveFindings : null;

  if (!showMessageThread && liveFindingsVisible == null) {
    return null;
  }

  const failedById = new Map(
    transport.messages
      .filter((message) => message.status === "failed")
      .map((message) => [transportMessageKey(message), message]),
  );

  return (
    <div data-testid="ask-v2-thread" data-presentation={presentation}>
      {showMessageThread ? (
        <AskPunttThread
          messages={shownMessages}
          presentation={presentation}
          chatId={threadChatId}
          waitingForAI={transport.inFlight}
          feedActorName={feedActorName}
          feedActorAvatar={feedActorAvatar}
          feedMembers={normalizedFeedMembers}
          currentUserId={currentUserId}
          // Withheld, not merely no-op'd, for a read-only viewer: AskPunttThread
          // derives `isLastAssistant` — and ChatAtoms its `interactive` — from
          // whether a submit handler exists at all, so handing it `undefined` is
          // what keeps the agent's question form from DRAWING a live Submit for
          // a viewer whose answer the backend would 403. The early return in
          // `submitAnswers` stays as defence in depth for any other call site.
          onSubmitAnswers={readOnly ? undefined : submitAnswers}
          // why: the form is locked by the same turn/upload states that refuse
          // its answer, and the reason tells the person what must finish or be
          // removed before submission can continue.
          formDisabled={formDisabledReason != null}
          formDisabledReason={formDisabledReason}
          chatLevelFileCount={attachmentDraft?.readyAttachments.length ?? 0}
          hideAgentChromeForMessage={(message) => {
            const transportMessage = transportById.get(message._id);
            return (
              transportMessage?.status === "aborted" &&
              message.role === "assistant"
            );
          }}
          renderBeforeMessage={(message) => {
            const hasQuoted = (message.quotedContexts?.length ?? 0) > 0;
            const hasComment = (message.commentContexts?.length ?? 0) > 0;
            const hasOpenItem = (message.openItemContexts?.length ?? 0) > 0;
            if (
              message.role !== "user" ||
              (!hasQuoted && !hasComment && !hasOpenItem)
            ) {
              return null;
            }
            return (
              <Flex gap="2" wrap="wrap" justify="end">
                {message.openItemContexts?.map((ctx) => (
                  <Flex
                    key={ctx.id}
                    align="center"
                    className="max-w-full rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                    data-testid={`ask-v2-sent-open-item-context-${message._id}-${ctx.id}`}
                  >
                    <Text
                      size="1"
                      className="max-w-[240px] truncate text-puntt-neutral-gray-11"
                    >
                      {ctx.needed}
                    </Text>
                  </Flex>
                ))}
                {message.commentContexts?.map((ctx) => (
                  <CommentContextChip
                    key={ctx.id}
                    context={ctx}
                    testId={`ask-v2-sent-comment-context-${message._id}-${ctx.id}`}
                  />
                ))}
                {message.quotedContexts?.map((ctx) => (
                  <Flex
                    key={ctx.id}
                    align="center"
                    className="max-w-full rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                    data-testid={`ask-v2-sent-quoted-context-${message._id}-${ctx.id}`}
                  >
                    <Text
                      size="1"
                      className="max-w-[200px] truncate text-puntt-neutral-gray-11"
                    >
                      {ctx.text}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            );
          }}
          renderAfterMessage={(message) => {
            const failed = failedById.get(message._id);
            if (failed) {
              return (
                <div className="mt-2 flex items-center gap-2">
                  <Text
                    as="p"
                    size="1"
                    color="red"
                    data-testid={`ask-v2-send-failed-${failed.clientId}`}
                  >
                    {t("askV2.transport.failed")}
                  </Text>
                  {/* Retry is a write: it re-POSTs the turn through
                    transport.retry, which the backend 403s for this viewer. A
                    read-only viewer CAN reach a failed row — AskV2ChatPage's
                    `canInteract` fails OPEN while history loads, so a send in
                    that window leaves a failed bubble that outlives the lock.
                    The page's gated transport then swallows the click in
                    silence, leaving a button that looks live and never does
                    anything. Drop the affordance and keep the failure notice,
                    matching the locked composer and the answer form above. */}
                  {readOnly ? null : (
                    <Button
                      size="1"
                      variant="ghost"
                      data-testid={`ask-v2-retry-${failed.clientId}`}
                      disabled={transport.inFlight || transport.sending}
                      onClick={() => {
                        void transport
                          .retry(failed.clientId)
                          .then((accepted) => {
                            if (accepted) {
                              attachmentDraft?.consumeAttachments(
                                failed.attachments ?? [],
                              );
                            }
                          });
                      }}
                    >
                      {t("askV2.transport.retry")}
                    </Button>
                  )}
                </div>
              );
            }

            const transportMessage = transportById.get(message._id);
            if (transportMessage?.status === "sending") {
              return (
                <Text
                  as="p"
                  size="1"
                  color="gray"
                  data-testid={`ask-v2-message-sending-${transportMessageKey(transportMessage)}`}
                >
                  {t("askV2.transport.sending")}
                </Text>
              );
            }
            if (transportMessage?.status === "aborted") {
              return (
                <Text as="p" size="1" color="gray">
                  {t("askV2.transport.stopped")}
                </Text>
              );
            }
            return null;
          }}
        />
      ) : null}
      {/* A streamed finding carries the workspace's judge control, which is a
        POST to christo (`useUpdateAskPunttReviewComment`) the backend 403s for
        this viewer — `AskV2ThreadFindings` cannot swallow it the way the gated
        transport swallows the retry, because it never goes through transport.
        So the flag rides through and the control is withheld, matching the
        answer form, the thumbs and the retry above. */}
      {liveFindingsVisible ? (
        <AskV2ThreadFindings
          findings={liveFindingsVisible}
          readOnly={readOnly}
        />
      ) : null}
    </div>
  );
}
